// The vocabulary of the tree of possible timelines: forks, prefixes, endings.
//
// A run is a path through a tree of executions. The root is the start of the
// program; wherever the scheduler had more than one runnable thread at the same
// instant it could have gone several ways, and each way is a child. A leaf is a
// terminal state. The trace on screen is one path from root to leaf.
//
// **A node is a forced prefix of picks.** `RunOptions.schedule` already says "at
// step i, run thread threads[i]", so a node is just that array, and running the
// program with it reaches the node and then lets the ordinary heuristic finish —
// which is what makes every node a complete execution rather than a fragment.
// Forcing a prefix the run took anyway is a no-op (a run is deterministic for a
// seed), so a branch splices one decision and leaves everything before it
// exactly as it was.
//
// **The tree the panel draws is no longer this one.** Per-*step* forks are
// exponential and mostly meaningless — `bank.stm` has 91 of them and not one
// changes the outcome — so the panel draws the enumeration's tree instead: one
// node per decision about which *unit* goes next, complete rather than sampled
// (`explore.ts`, and docs/design/txn-granular-exploration.md). What is left here
// is what both need: the forks a run had, the prefix that reaches a node, and
// what an execution came to. See docs/design/timeline-tree.md.

import type { Causality } from "../src/backend/interp/causality.js";
import type { RunResult } from "../src/backend/interp/interpreter.js";

/** A step where the scheduler had a real choice — a branch point of the tree. */
export interface Fork {
  /** Step index, in the run this fork was read from. */
  step: number;
  /** The thread that ran here. */
  taken: number;
  /** Every thread that could have, best tie-break first, so `options[0]` is what
   *  the unforced heuristic takes. Always contains `taken`. */
  options: number[];
}

/** The first fork strictly after `step`, or null when there is none left — from
 *  there the scheduler makes no more choices at all. */
export function nextFork(c: Causality, after: number): Fork | null {
  for (let i = after + 1; i < c.steps.length; i++) {
    const s = c.steps[i]!;
    if (s.choices.length > 1) return { step: i, taken: s.thread, options: s.choices };
  }
  return null;
}

/**
 * Every fork of a run, in order.
 *
 * No longer a tree of nodes — the panel reasons about units, not steps — but
 * still the *yardstick* that claim is measured against, and the tests hold it
 * there: `bank.stm` has 91 of these and not one of them is a decision the
 * enumeration has to make, which is the whole argument for the collapse.
 */
export function forksOf(c: Causality): Fork[] {
  const out: Fork[] = [];
  for (let f = nextFork(c, -1); f; f = nextFork(c, f.step)) out.push(f);
  return out;
}

/** The picks a run made, up to but not including `step`. */
export function picksBefore(c: Causality, step: number): number[] {
  return c.steps.slice(0, step).map((s) => s.thread);
}

/** Whether `prefix` is what this run actually did — i.e. whether the run is the
 *  one that passes through that node. */
export function onPath(c: Causality, prefix: readonly number[]): boolean {
  if (prefix.length > c.steps.length) return false;
  return prefix.every((t, i) => c.steps[i]!.thread === t);
}

/** One assertion's verdict on a timeline: its label and whether it held. */
export interface AssertResult {
  message: string;
  passed: boolean;
}

/** Where a timeline came out: everything about a terminal state worth comparing
 *  one against another. */
export interface Ending {
  /** Every `assert` the run reached, in order, with the verdict it produced. This
   *  is the outcome the race detector keys on: a fork where the same assertion
   *  passes down one branch and fails down another is exactly a divergence. */
  asserts: AssertResult[];
  /** Everything the program printed, in order. */
  prints: string[];
  /** The last value written to each address — the final heap, as the trace
   *  already rendered it. */
  final: Map<string, string>;
  commits: number;
  aborts: number;
}

export function endingOf(result: RunResult): Ending {
  const asserts: AssertResult[] = [];
  const prints: string[] = [];
  const final = new Map<string, string>();
  for (const ev of result.trace.events) {
    if (ev.type === "assert") asserts.push({ message: ev.message, passed: ev.passed });
    else if (ev.type === "print") prints.push(ev.message);
    else if (ev.type === "write") final.set(ev.addr, ev.value);
  }
  return { asserts, prints, final, commits: result.commits, aborts: result.aborts };
}

/** A timeline's assertion verdicts as one badge string — a check or cross per
 *  assertion, in order (`✓ a  ✗ b`). Two timelines that differ in *any*
 *  assertion's verdict get different labels, which is what makes a divergence
 *  legible on a tree node. Empty when the run reached no assertion. */
export function assertLabel(asserts: readonly AssertResult[]): string {
  return asserts.map((a) => `${a.passed ? "✓" : "✗"}${a.message ? ` ${a.message}` : ""}`).join("  ");
}

/**
 * What a timeline came to, for the node's badge.
 *
 * **Assertions first, then the program's last `print`.** An `assert` is the
 * sharpest thing a program can say about how it ended — a named predicate that
 * either held or didn't — so when a run reached any, its verdicts *are* the
 * badge (`✗ balance reflects both withdrawals`). That is the point of assertions here: the
 * detector searches the timeline tree for a fork where one branch's label reads
 * `✓` and the other's `✗`. A run with no assertion falls back to its last
 * `print`, and one with neither gets no badge — the honest answer when a program
 * declines to say how it finished.
 *
 * **Its own words, and nothing else.** The playground could derive something
 * from the final heap instead, and it would be guessing: on `game.stm` the guess
 * is `/player/1 = Player{pos: Vec2{x: 2.0, y: 0.0}, status: Dead(killed_by: /3)}`
 * — long, arbitrary, and less true than whatever the author would have asserted
 * or printed if they cared.
 *
 * This began as a *comparison* — "differs from the run on screen" — which cost
 * two rounds of confusion to unlearn. Comparing against the displayed run made
 * every label in the panel change as the cursor moved; comparing against a fixed
 * run fixed that but left `same ending` on screen with no way to tell what it
 * was the same *as*. A label that needs a referent needs the referent visible,
 * and there isn't room. Two branches reading `✓ balance reflects both withdrawals` and
 * `✗ balance reflects both withdrawals` say everything the comparison did, without a vocabulary.
 */
export function endingLabel(result: RunResult): string {
  const e = endingOf(result);
  if (e.asserts.length) return assertLabel(e.asserts);
  return e.prints.at(-1) ?? "";
}
